/**
 * SectionSuggestions
 *
 * Industry-aware section recommendations panel.
 * Fetches suggestions from the backend and allows one-click adding.
 */

import { useState, useEffect, useCallback } from 'react';
import { getSectionSuggestions, applyStructuralChanges } from '../../api/ai-editor';

// =============================================================================
// TYPES
// =============================================================================

interface SectionSuggestionsProps {
  websiteId: string;
  activePage: string;
  onContentChange: () => void;
}

const SECTION_LABELS: Record<string, string> = {
  hero: 'Hero',
  services: 'Services',
  about: 'About',
  testimonials: 'Testimonials',
  gallery: 'Gallery',
  stats: 'Stats',
  credentials: 'Credentials',
  process: 'Process',
  team: 'Team',
  cta: 'Call to Action',
  faq: 'FAQ',
  location: 'Location',
  contact: 'Contact',
};

// =============================================================================
// COMPONENT
// =============================================================================

export function SectionSuggestions({
  websiteId,
  activePage,
  onContentChange,
}: SectionSuggestionsProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addingType, setAddingType] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getSectionSuggestions(websiteId, activePage)
      .then((result) => {
        if (!cancelled) {
          setSuggestions(result.suggestions || []);
        }
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [websiteId, activePage]);

  const handleAdd = useCallback(async (sectionType: string) => {
    if (addingType) return;
    setAddingType(sectionType);
    try {
      const result = await applyStructuralChanges(websiteId, [{
        id: crypto.randomUUID(),
        action: 'add_section',
        target: { page: activePage },
        data: { type: sectionType },
        position: 'end',
      }]);
      if (result.success) {
        setSuggestions((prev) => prev.filter((s) => s !== sectionType));
        onContentChange();
      }
    } catch {
      // Silently fail — user can retry
    } finally {
      setAddingType(null);
    }
  }, [websiteId, activePage, addingType, onContentChange]);

  if (isLoading) {
    return (
      <div className="px-4 py-3">
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
          Suggested Sections
        </div>
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-7 w-20 bg-gray-100 rounded-full animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) return null;

  return (
    <div className="px-4 py-3 border-t border-gray-100">
      <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
        Suggested Sections
      </div>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((type) => (
          <button
            key={type}
            onClick={() => handleAdd(type)}
            disabled={addingType !== null}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors disabled:opacity-50"
          >
            {addingType === type ? (
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            )}
            {SECTION_LABELS[type] || type}
          </button>
        ))}
      </div>
    </div>
  );
}
